import React, { useEffect, useState, useContext, useMemo } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { Avatar } from '@mui/material';
import Logo from '../../../assets/images/logo.png';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { TextField, Button, Stack } from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CloseIcon from '@mui/icons-material/Close';
import '../../../assets/styles/phonecontainer.css';
import {
    CALLS_ID_INBOUND_SESSION_KEY,
    CALLS_ID_SESSION_CHANGED,
    CALLS_ID_SESSION_KEY,
    AIRCALL_USER_ID_SESSION_CHANGED,
    AIRCALL_USER_ID_SESSION_KEY,
    clearCallsId,
    clearInboundCallsId,
} from '../../../utils/callsIdSessionSync';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Autocomplete } from '@mui/material';
import { useGetFacilitiesQuery } from '../../../api/endpoints/apiConfig';
import { useLookupActiveAircallCall } from '../../../hooks/useLookupActiveAircallCall';
import { FacilityLocationCode } from '../../../../src/models/FacilityLocationCode';
import { FacilityContext } from '../../../context/FacilityContext';
import { setIsDisposition, setisModal } from '../../../store/reducers/dispositionSlice';
type Props = {
    isvisible: boolean;
};
const AIRCALL_PANEL_HIDDEN_PATHS = ['/', '/WaitingPage', '/TestOperator'];

const Header: React.FC<Props> = ({ isvisible }) => {
    const phoneVisible = useAppSelector((state) => state.phone.phoneVisible);
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const showAirCallPanel = !AIRCALL_PANEL_HIDDEN_PATHS.includes(pathname);
    const dispatch = useAppDispatch();
    const [facilities, setFacilities] = useState<{ label: string; id: number }[]>([]);
    const [firstName, setFirstName] = useState<string | null>(null);
    const [lastName, setLastName] = useState<string | null>(null);
    const selectedOperatorId = sessionStorage.getItem('operatorId');
    const operatorId = selectedOperatorId !== null ? parseInt(selectedOperatorId, 10) : 1;
    const { data: Allfacility } = useGetFacilitiesQuery(operatorId);
    const context = useContext(FacilityContext);
    const isDisposition = useAppSelector((state) => state.disposition.isDisposition);
    const [sessionCallsId, setSessionCallsId] = useState<string | null>(() =>
        sessionStorage.getItem(CALLS_ID_SESSION_KEY)
    );
    const [sessionCallsIdInbound, setSessionCallsIdInbound] = useState<string | null>(() =>
        sessionStorage.getItem(CALLS_ID_INBOUND_SESSION_KEY)
    );

    useEffect(() => {
        const syncFromSession = () => {
            setSessionCallsId(sessionStorage.getItem(CALLS_ID_SESSION_KEY));
            setSessionCallsIdInbound(sessionStorage.getItem(CALLS_ID_INBOUND_SESSION_KEY));
        };
        syncFromSession();
        window.addEventListener(CALLS_ID_SESSION_CHANGED, syncFromSession);
        const onStorage = (e: StorageEvent) => {
            if (e.key === CALLS_ID_SESSION_KEY || e.key === CALLS_ID_INBOUND_SESSION_KEY) {
                syncFromSession();
            }
        };
        window.addEventListener('storage', onStorage);
        return () => {
            window.removeEventListener(CALLS_ID_SESSION_CHANGED, syncFromSession);
            window.removeEventListener('storage', onStorage);
        };
    }, []);

    const [aircallUserIdDisplay, setAircallUserIdDisplay] = useState(() =>
        sessionStorage.getItem(AIRCALL_USER_ID_SESSION_KEY) ?? ''
    );

    useEffect(() => {
        const syncUserId = () =>
            setAircallUserIdDisplay(sessionStorage.getItem(AIRCALL_USER_ID_SESSION_KEY) ?? '');
        syncUserId();
        window.addEventListener(AIRCALL_USER_ID_SESSION_CHANGED, syncUserId);
        const onStorage = (e: StorageEvent) => {
            if (e.key === AIRCALL_USER_ID_SESSION_KEY) syncUserId();
        };
        window.addEventListener('storage', onStorage);
        return () => {
            window.removeEventListener(AIRCALL_USER_ID_SESSION_CHANGED, syncUserId);
            window.removeEventListener('storage', onStorage);
        };
    }, []);

    const handleClearCallId = () => {
        clearCallsId();
    };

    const handleClearInboundCallId = () => {
        clearInboundCallsId();
    };

    const { lookupActiveCall, isLookupSearching } = useLookupActiveAircallCall();

    const handleFixItClick = () => {
        void lookupActiveCall();
    };

    // Ensure the context is available
    if (!context) {
        throw new Error('FacilityContext must be used within a FacilityProvider');
    }
    const { selectedFacility, setSelectedFacility } = context;

    // Update facilities based on Allfacility or sessionStorage
    useEffect(() => {
        if (Allfacility) {
            sessionStorage.setItem("Allfacility", JSON.stringify(Allfacility));
        }
    }, []);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        let fromSession: FacilityLocationCode[] = [];
        try {
            const raw = sessionStorage.getItem("Allfacility");
            const parsed = raw ? JSON.parse(raw) : null;
            if (parsed && typeof parsed === "object" && Array.isArray((parsed as { facilities?: unknown }).facilities)) {
                fromSession = (parsed as { facilities: FacilityLocationCode[] }).facilities;
            } else if (Array.isArray(parsed)) {
                fromSession = parsed as FacilityLocationCode[];
            }
        } catch {
            fromSession = [];
        }
        const apiFacilities = Allfacility?.facilities;
        const facilitiesData = Array.isArray(apiFacilities) ? apiFacilities : fromSession;

        if (Array.isArray(facilitiesData)) {
            const facilityOptions = facilitiesData
                .filter((facility) => facility.name)
                .map((facility) => ({
                    label: `${facility.location_code} - ${facility.name}`,
                    id: facility.id,
                    location: facility.location_code
                })) // include location
                .sort((a, b) => a.location.localeCompare(b.location)); // sort by location
            setFacilities(facilityOptions);
        }
    }, [Allfacility]);

    // After refresh, context may only have { id } until options load; align label with the list for Autocomplete display.
    useEffect(() => {
        if (!selectedFacility.id || facilities.length === 0) return;
        const match = facilities.find((f) => f.id === selectedFacility.id);
        if (match && match.label !== selectedFacility.label) {
            setSelectedFacility({ id: match.id, label: match.label });
        }
    }, [facilities, selectedFacility.id, selectedFacility.label, setSelectedFacility]);

    const facilityAutocompleteValue = useMemo((): { label: string; id: number } | undefined => {
        if (!selectedFacility.id) return undefined;
        const fromList = facilities.find((f) => f.id === selectedFacility.id);
        return fromList ?? selectedFacility;
    }, [facilities, selectedFacility]);

    useEffect(() => {
        const storedFirstName = sessionStorage.getItem('FirstName');
        const storedLastName = sessionStorage.getItem('LastName');
        if (storedFirstName) setFirstName(storedFirstName);
        if (storedLastName) setLastName(storedLastName);
    }, []);
    const handleFacilityChange = (newValue: { label: string; id: number } | null) => {
        if (newValue) {
            setSelectedFacility(newValue);
            //sessionStorage.setItem("matchedFacilityName", newValue.label);
            sessionStorage.setItem("matchedFacilityId", String(newValue.id));
            navigate(`/Dashboard/${newValue.id}`);
        }
    };
    return (
        <>
        <AppBar position="sticky">
            <Toolbar sx={{ justifyContent: 'space-between', paddingBlock: 2, gap: 2 }}>
                {(isvisible || showAirCallPanel) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        {isvisible && (
                            <Box display={'inline-flex'} alignItems={'center'}>
                                <Box component={'span'} display={'inline-flex'} alignItems={'center'} sx={{ cursor: 'pointer' }}>
                                    <Link to="/WaitingPage">
                                        <Typography component={'img'} src={Logo} alt="Logo" />
                                    </Link>
                                </Box>
                            </Box>
                        )}
                        {showAirCallPanel && (
                            <Box
                                sx={{
                                    ml: isvisible ? '100px' : 0,
                                    pt: 1,
                                    minWidth: 220,
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'relative',
                                        border: '1px solid',
                                        borderColor: 'rgba(255, 255, 255, 0.24)',
                                        borderRadius: 1,
                                        px: 1.5,
                                        pt: 1.25,
                                        pb: 1,
                                        bgcolor: 'primary.main',
                                        minWidth: 220,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        component="span"
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 12,
                                            transform: 'translateY(-50%)',
                                            zIndex: 1,
                                            px: 0.75,
                                            bgcolor: 'primary.main',
                                            fontWeight: 700,
                                            lineHeight: 1,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Air Call
                                    </Typography>
                                    <Stack spacing={1}>
                                       
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                                            <Typography variant="body2" component="span">
                                                User ID: {aircallUserIdDisplay}
                                            </Typography>
                                            <Typography variant="body2" component="span">
                                                Inbound Call ID:
                                            </Typography>
                                            {sessionCallsIdInbound ? (
                                                <CheckBoxIcon sx={{ color: 'success.main', fontSize: 22 }} />
                                            ) : (
                                                <CloseIcon sx={{ color: 'error.main', fontSize: 22 }} />
                                            )}
                                            <Typography variant="body2" component="span">
                                                {sessionCallsIdInbound ?? ''}
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                                            <Typography variant="body2" component="span">
                                                Call ID:
                                            </Typography>
                                            {sessionCallsId ? (
                                                <CheckBoxIcon sx={{ color: 'success.main', fontSize: 22 }} />
                                            ) : (
                                                <CloseIcon sx={{ color: 'error.main', fontSize: 22 }} />
                                            )}
                                            <Typography variant="body2" component="span">
                                                {sessionCallsId ?? ''}
                                            </Typography>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={handleFixItClick}
                                                disabled={isLookupSearching}
                                                sx={{
                                                    ml: 0.5,
                                                    minWidth: 'auto',
                                                    py: 0.125,
                                                    px: 1,
                                                    fontSize: '0.8125rem',
                                                    fontWeight: 400,
                                                    textTransform: 'none',
                                                    borderColor: 'rgba(255, 255, 255, 0.35)',
                                                    color: 'rgba(255, 255, 255, 0.75)',
                                                    '&:hover': {
                                                        borderColor: 'rgba(255, 255, 255, 0.55)',
                                                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                                        color: 'rgba(255, 255, 255, 0.95)',
                                                    },
                                                    '&.Mui-disabled': {
                                                        borderColor: 'rgba(255, 255, 255, 0.2)',
                                                        color: 'rgba(255, 255, 255, 0.35)',
                                                    },
                                                }}
                                            >
                                                Refresh
                                            </Button>
                                        </Stack>
                                        {window.config.appEnv !== 'production' && (
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                                                <Typography variant="body2" component="span">
                                                    Test:
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="secondary"
                                                    onClick={handleClearCallId}
                                                >
                                                    Clear Call ID
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="secondary"
                                                    onClick={handleClearInboundCallId}
                                                >
                                                    Clear Inbound Call ID
                                                </Button>
                                            </Stack>
                                        )}
                                    </Stack>
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}

                <Box mx={'auto'} sx={{ display: 'inline-flex', gap: 3, alignItems: 'center' }}>
                    <Box>
                        <form action="#">
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                                {isvisible && (<Autocomplete
                                    disablePortal
                                    options={facilities}
                                    getOptionLabel={(option) => option.label}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    value={facilityAutocompleteValue}
                                    sx={{
                                        backgroundColor: 'white',
                                        '& .MuiInputBase-root': {
                                            backgroundColor: 'white',
                                        },
                                        width: 300,
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Select Facility"
                                            inputProps={{
                                                ...params.inputProps,
                                                readOnly: true, // Disables manual input
                                            }}
                                        />
                                    )}
                                    size="small"
                                    onChange={(event, newValue) => handleFacilityChange(newValue)} // Ensure this works properly
                                    freeSolo={false}
                                    disableClearable
                                    openOnFocus
                                />
                                )}
                                {isvisible && (<Button disableElevation variant='contained' color='secondary' onClick={() => navigate('/Facility')}>
                                    Facility Search
                                </Button>)}
                            </Stack>
                        </form>
                    </Box>
                    <Box>
                        <Box sx={{ display: 'inline-flex', gap: 1.5, alignItems: 'center' }}>
                            {isvisible && (<Avatar sx={{ bgcolor: '#0053FF' }}>
                                {firstName && lastName ? `${firstName[0]}${lastName[0]}` : ''}
                            </Avatar>)}
                            {isvisible && (<Typography component={'span'}>
                                {firstName && lastName ? `${firstName} ${lastName}` : ''}
                            </Typography>)}
                        </Box>
                    </Box>
                    {isDisposition && (                   
                        <Button disableElevation variant='contained' color='secondary' onClick={() => {
                            dispatch(setIsDisposition(!isDisposition)); 
                            dispatch(setisModal(isDisposition)); 
                          }}>
                        Disposition
                    </Button>
                   )}

                    <div id="phone-container" className={phoneVisible ? 'phone-container' : 'd-none'}>
                        <div className='arrow-up'></div>
                        <div id="phone"></div>
                    </div>
                </Box>
            </Toolbar>
            
        </AppBar>
        </>
    );
};

export default Header;
